const ArgumentType = require('../../extension-support/argument-type');
const BlockType = require('../../extension-support/block-type');
const Cast = require('../../util/cast');
const formatMessage = require('format-message');

const blockIconURI = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB4PSIzIiB5PSIzIiB3aWR0aD0iMzQiIGhlaWdodD0iMzQiIHJ4PSI4IiBmaWxsPSIjRjQ4MTIwIi8+PGNpcmNsZSBjeD0iMjAiIGN5PSIxMCIgcj0iNCIgZmlsbD0id2hpdGUiLz48cGF0aCBkPSJNMTkgMTVDMTkgMTQuNDQ3NyAxOS40NDc3IDE0IDIwIDE0QzIwLjU1MjMgMTQgMjEgMTQuNDQ3NyAyMSAxNVYyMEMyMSAyMC41NTIzIDIxLjQ0NzcgMjEgMjIgMjFIMjZDMjYuNTUyMyAyMSAyNyAyMS40NDc3IDI3IDIyQzI3IDIyLjU1MjMgMjYuNTUyMyAyMyAyNiAyM0gyM1YyN0wyNy44Mjg0IDMyLjY1NjhDMjguMTg3OSAzMy4wNzgxIDI4LjEzNzkgMzMuNzA5NCAyNy43MTY2IDM0LjA2ODlDMjcuMjk1NCAzNC40Mjg0IDI2LjY2NDEgMzQuMzc4NCAyNi4zMDQ2IDMzLjk1NzFMMjEgMjcuNzM4NVYzNEMyMSAzNC41NTIzIDIwLjU1MjMgMzUgMjAgMzVDMTkuNDQ3NyAzNSAxOSAzNC41NTIzIDE5IDM0VjI3LjczODVMMTMuNjk1NCAzMy45NTcxQzEzLjMzNTkgMzQuMzc4NCAxMi43MDQ2IDM0LjQyODQgMTIuMjgzNCAzNC4wNjg5QzExLjg2MjEgMzMuNzA5NCAxMS44MTIxIDMzLjA3ODEgMTIuMTcxNiAzMi42NTY4TDE3IDI3VjIzSDE0QzEzLjQ0NzcgMjMgMTMgMjIuNTUyMyAxMyAyMkMxMyAyMS40NDc3IDEzLjQ0NzcgMjEgMTQgMjFIMThDMTguNTUyMyAyMSAxOSAyMC41NTIzIDE5IDIwVjE1WiIgZmlsbD0id2hpdGUiLz48L3N2Zz4=';
const menuIconURI = blockIconURI;

const VIDEO_STATE = {
    OFF: 'off',
    ON: 'on',
    ON_FLIPPED: 'on-flipped'
};

const BODY_PART_MENU = [
    ['næse', 'nose'],
    ['venstre øje', 'leftEye'],
    ['højre øje', 'rightEye'],
    ['venstre øre', 'leftEar'],
    ['højre øre', 'rightEar'],
    ['venstre skulder', 'leftShoulder'],
    ['højre skulder', 'rightShoulder'],
    ['venstre albue', 'leftElbow'],
    ['højre albue', 'rightElbow'],
    ['venstre håndled', 'leftWrist'],
    ['højre håndled', 'rightWrist'],
    ['venstre hofte', 'leftHip'],
    ['højre hofte', 'rightHip'],
    ['venstre knæ', 'leftKnee'],
    ['højre knæ', 'rightKnee'],
    ['venstre ankel', 'leftAnkle'],
    ['højre ankel', 'rightAnkle']
];

const loadScriptOnce = (url, globalName) => {
    const globalScope = typeof window !== 'undefined' ? window : global;
    if (globalScope[globalName]) return Promise.resolve(globalScope[globalName]);

    globalScope.__datakoderenScriptPromises = globalScope.__datakoderenScriptPromises || {};
    if (globalScope.__datakoderenScriptPromises[url]) return globalScope.__datakoderenScriptPromises[url];

    globalScope.__datakoderenScriptPromises[url] = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = url;
        script.async = true;
        script.onload = () => resolve(globalScope[globalName]);
        script.onerror = () => reject(new Error(`Kunne ikke hente scriptet ${url}`));
        document.head.appendChild(script);
    });

    return globalScope.__datakoderenScriptPromises[url];
};

class Scratch3BodySensingBlocks {
    constructor (runtime) {
        this.runtime = runtime;
        this._loopHandle = null;
        this._posenetModelPromise = null;
        this._latestPose = null;
        this._isRunningInference = false;
        this._dimensions = [480, 360];
        this._videoState = VIDEO_STATE.ON;
        this._videoTransparency = 50;

        this._startLoop();
    }

    getInfo () {
        return {
            id: 'bodySensing',
            name: formatMessage({
                id: 'bodySensing.categoryName',
                default: 'Krop',
                description: 'Navn på kropssensing-udvidelsen'
            }),
            blockIconURI,
            menuIconURI,
            color1: '#F48120',
            color2: '#D86C11',
            color3: '#B75809',
            blocks: [
                {
                    opcode: 'goToBodyPart',
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: 'bodySensing.goToBodyPart',
                        default: 'gå til [BODY_PART]',
                        description: 'Flyt sprite til en kropsdel'
                    }),
                    arguments: {
                        BODY_PART: {
                            type: ArgumentType.STRING,
                            menu: 'BODY_PART',
                            defaultValue: 'nose'
                        }
                    }
                },
                {
                    opcode: 'bodyDetected',
                    blockType: BlockType.BOOLEAN,
                    text: formatMessage({
                        id: 'bodySensing.bodyDetected',
                        default: 'kan se krop?',
                        description: 'Om der er fundet en krop'
                    })
                },
                {
                    opcode: 'bodyPartX',
                    blockType: BlockType.REPORTER,
                    text: formatMessage({
                        id: 'bodySensing.bodyPartX',
                        default: 'x for [BODY_PART]',
                        description: 'X-position for en kropsdel'
                    }),
                    arguments: {
                        BODY_PART: {
                            type: ArgumentType.STRING,
                            menu: 'BODY_PART',
                            defaultValue: 'nose'
                        }
                    }
                },
                {
                    opcode: 'bodyPartY',
                    blockType: BlockType.REPORTER,
                    text: formatMessage({
                        id: 'bodySensing.bodyPartY',
                        default: 'y for [BODY_PART]',
                        description: 'Y-position for en kropsdel'
                    }),
                    arguments: {
                        BODY_PART: {
                            type: ArgumentType.STRING,
                            menu: 'BODY_PART',
                            defaultValue: 'nose'
                        }
                    }
                },
                {
                    opcode: 'setVideoState',
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: 'bodySensing.setVideoState',
                        default: 'sæt kamera [VIDEO_STATE]',
                        description: 'Tænd eller sluk kameraet'
                    }),
                    arguments: {
                        VIDEO_STATE: {
                            type: ArgumentType.STRING,
                            menu: 'VIDEO_STATE',
                            defaultValue: VIDEO_STATE.ON
                        }
                    }
                },
                {
                    opcode: 'setVideoTransparency',
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: 'bodySensing.setVideoTransparency',
                        default: 'sæt kameragennemsigtighed til [TRANSPARENCY]',
                        description: 'Sæt kameraets gennemsigtighed'
                    }),
                    arguments: {
                        TRANSPARENCY: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 50
                        }
                    }
                }
            ],
            menus: {
                BODY_PART: BODY_PART_MENU.map(([text, value]) => ({text, value})),
                VIDEO_STATE: [
                    {text: 'tændt', value: VIDEO_STATE.ON},
                    {text: 'slukket', value: VIDEO_STATE.OFF},
                    {text: 'tændt spejlet', value: VIDEO_STATE.ON_FLIPPED}
                ]
            }
        };
    }

    _startLoop () {
        if (this._loopHandle) return;
        this._loopHandle = setInterval(() => {
            this._stepLoop().catch(() => {});
        }, 120);
    }

    _getVideoDevice () {
        return this.runtime.ioDevices && this.runtime.ioDevices.video;
    }

    _applyVideoState () {
        const video = this._getVideoDevice();
        if (!video) return;

        if (this._videoState === VIDEO_STATE.OFF) {
            video.disableVideo();
            return;
        }

        video.enableVideo();
        video.mirror = this._videoState !== VIDEO_STATE.ON_FLIPPED;
        video.setPreviewGhost(this._videoTransparency);
    }

    async _ensureModel () {
        if (this._posenetModelPromise) return this._posenetModelPromise;

        this._posenetModelPromise = loadScriptOnce(
            'https://cdn.jsdelivr.net/npm/@tensorflow/tfjs@3.21.0/dist/tf.min.js',
            'tf'
        ).then(() => loadScriptOnce(
            'https://cdn.jsdelivr.net/npm/@tensorflow-models/posenet@2.2.2/dist/posenet.min.js',
            'posenet'
        )).then(posenet => posenet.load());

        return this._posenetModelPromise;
    }

    async _stepLoop () {
        if (this._isRunningInference) return;
        const video = this._getVideoDevice();
        if (!video || this._videoState === VIDEO_STATE.OFF) return;

        const frame = video.getFrame({
            format: 'image-data',
            dimensions: this._dimensions
        });
        if (!frame) return;

        this._isRunningInference = true;
        try {
            const model = await this._ensureModel();
            this._latestPose = await model.estimateSinglePose(frame, {
                flipHorizontal: false
            });
        } finally {
            this._isRunningInference = false;
        }
    }

    _hasPose () {
        return this._latestPose && this._latestPose.keypoints && this._latestPose.score > 0.01;
    }

    _toScratchCoordinates (position) {
        return {
            x: position.x - (this._dimensions[0] / 2),
            y: (this._dimensions[1] / 2) - position.y
        };
    }

    _getBodyPartPosition (bodyPart) {
        if (!this._hasPose()) return null;
        const keypoint = this._latestPose.keypoints.find(point => point.part === bodyPart);
        if (!keypoint || !keypoint.position) return null;
        return this._toScratchCoordinates(keypoint.position);
    }

    goToBodyPart (args, util) {
        const position = this._getBodyPartPosition(Cast.toString(args.BODY_PART));
        if (!position || !util.target || !util.target.setXY) return;
        util.target.setXY(position.x, position.y, false);
    }

    bodyDetected () {
        return Boolean(this._hasPose());
    }

    bodyPartX (args) {
        const position = this._getBodyPartPosition(Cast.toString(args.BODY_PART));
        return position ? Math.round(position.x) : 0;
    }

    bodyPartY (args) {
        const position = this._getBodyPartPosition(Cast.toString(args.BODY_PART));
        return position ? Math.round(position.y) : 0;
    }

    setVideoState (args) {
        const state = Cast.toString(args.VIDEO_STATE);
        this._videoState = Object.values(VIDEO_STATE).includes(state) ? state : VIDEO_STATE.ON;
        this._applyVideoState();
    }

    setVideoTransparency (args) {
        this._videoTransparency = Math.max(0, Math.min(100, Cast.toNumber(args.TRANSPARENCY)));
        this._applyVideoState();
    }
}

module.exports = Scratch3BodySensingBlocks;
